import { elementDefinitions, elementTypes } from 'crazyEditor/components/Elements/index';
import _ from 'lodash';
import { store } from 'crazyEditor/redux/store';
import {getNormalizedTemplateTagSpecifics} from 'crazyEditor/redux/modules/templateTagSet/templateTagSelectors';
import { getIsAdmin } from 'crazyEditor/redux/modules/user/userSelectors';
import {defaultMemoize} from 'reselect';

export function getElementMetaData(elementType) {
  return elementDefinitions[elementType].metaData;
}

export function isChildElement(element) {
  return !!element.parentId;
}

function getElementDefinitionPropsInternal(elementProps, applyTemplateTagProps, richText, limitPageWidth, isChild, mobilePage, applyPublish) {
  if (applyTemplateTagProps) {
    elementProps = {
      ...elementProps,
      ...elementProps.templateTagProps,
    };
    if (richText) {
      elementProps.ckfinderEnabled = true;
    }
  }
  if (limitPageWidth) {
    elementProps = {
      ...elementProps,
      ...elementProps.limitPageWidth,
    };
  }
  if (isChild) {
    elementProps = {
      ...elementProps,
      ...elementProps.child,
    };
  }
  if (mobilePage) {
    elementProps = {
      ...elementProps,
      ...elementProps.mobileProps
    };
  }
  if (applyPublish) {
    elementProps = {
      ...elementProps,
      ...elementProps.publish
    };
  }
  return elementProps;
}
const memoizeFunctions = {};
function getElementDefinitionPropsMemoized(...args) {
  if (!memoizeFunctions[args]) {
    memoizeFunctions[args] = defaultMemoize(getElementDefinitionPropsInternal);
  }
  return memoizeFunctions[args];
}

export function getElementDefinitionProps(elementType, options = false) {
  let element;
  let isChild;
  let isMobile = options;
  let publish = false;
  if (_.isObject(elementType)) {
    element = elementType;
    elementType = element.type;
    isMobile = !!element.desktopId;
    isChild = isChildElement(element);
  }
  if (_.isObject(options)) {
    isMobile = options.isMobile;
    publish = options.publish;
  }
  const elementProps = elementDefinitions[elementType].elementProps;
  const state = store && store.getState();
  const applyTemplateTagProps = element && element.data && (element.data.templateTagData || (state && getIsAdmin(state) && _.has(element, 'data.templateTag'))) && elementProps.templateTagProps;
  const richText = _.get(element, 'data.templateTagData.richText');
  isChild = isChild && elementProps.child;
  const limitPageWidth = state && state.user.limitPageWidth;
  const mobilePage = isMobile && elementTypes.PageElement === elementType;
  const applyPublish = publish && elementProps.publish;
  return getElementDefinitionPropsMemoized(elementType, applyTemplateTagProps, richText, limitPageWidth, isChild, mobilePage, applyPublish)(elementProps, applyTemplateTagProps, richText, limitPageWidth, isChild, mobilePage, applyPublish);
}

export function getElementMethods(elementType) {
  return elementDefinitions[elementType].methods;
}

export function getElementName(element) {
  if (_.isString(element)) {
    return getElementMetaData(element).name;
  }
  let displayName;
  if (element.data) {
    const {templateTagData, templateTag} = element.data;
    if (templateTag) {
      displayName = getNormalizedTemplateTagSpecifics(store.getState())[templateTag].displayName;
    }
    if (!displayName && templateTagData) {
      displayName = templateTagData.displayName;
    }
  }
  if (displayName) {
    return displayName;
  }
  return getElementMetaData(element.type).name;
}

export function getElementTooltip(element) {
  if (_.isString(element)) {
    return getElementMetaData(element).tooltip;
  }
  if (element.data) {
    const {templateTag} = element.data;
    if (templateTag) {
      const {tooltip} = getNormalizedTemplateTagSpecifics(store.getState())[templateTag];
      if (tooltip) {
        return tooltip;
      }
    }
  }
  return getElementMetaData(element.type).tooltip;
}

export function getElementIcon(element) {
  if (_.isString(element)) {
    return getElementMetaData(element).icon;
  }
  if (element.data) {
    const {templateTag} = element.data;
    if (templateTag) {
      const {icon} = getNormalizedTemplateTagSpecifics(store.getState())[templateTag];
      if (icon) {
        return icon;
      }
    }
  }
  return getElementMetaData(element.type).icon;
}

export function isTextElement(elementType) {
  return !!getElementDefinitionProps(elementType).isTextElement;
}

export function isFullWidth(elementType) {
  return !!getElementDefinitionProps(elementType).fullWidth;
}

export function shouldPreserveAspectRatio(elementType) {
  return !!getElementDefinitionProps(elementType).preserveAspectRatio;
}

export function getEditModeTitle(element) {
  if (isTextElement(element.type) && element.data.templateTagData && element.data.templateTagData.displayName) {
    return `Edit ${element.data.templateTagData.displayName}`;
  }
  return getElementMetaData(element.type).editModeTitle;
}

export function getElementPreset(elementType, presetInd = 0) {
  const elementPreset = _.cloneDeep(elementDefinitions[elementType].presets[presetInd]);
  if (elementType === elementTypes.GalleryElement && !_.isEmpty(store.getState().templateTagSet)) {
    elementPreset.data.templateTag = elementTypes.GalleryElement;
  }
  return elementPreset;
}

export function getChildElementPreset(elementType, presetInd = 0) {
  return _.cloneDeep(elementDefinitions[elementType].childPresets[presetInd]);
}

export function getDesktopPagePreset(presetInd = 0) {
  return elementDefinitions[elementTypes.PageElement].presets.desktop[presetInd];
}

export function getMobilePagePreset(presetInd = 0) {
  return elementDefinitions[elementTypes.PageElement].presets.mobile[presetInd];
}

export function getBackgroundElementPreset(presetInd = 0) {
  return getElementPreset(elementTypes.BackgroundElement, presetInd);
}

export function getElementPanel(elementType) {
  return elementDefinitions[elementType].panel;
}

export function hasElementPanel(elementType) {
  return !!elementDefinitions[elementType].panel;
}

export function isFontSelectable(element) {
  return elementDefinitions[element.type].elementProps.fontSelectable || (elementTypes.RichTextElement === element.type && element.desktopId);
}

export function isHyperlinkEnabled(elementType) {
  return elementDefinitions[elementType].elementProps.hyperlinkEnabled;
}
